/**
 * What a MOVE commits (F-32/F-36/F-34) — PURE logic, no React, no Yjs.
 *
 * A move RESETS an element's belonging from where it was DROPPED: into the cell
 * the pointer was over, else the one under its top-left, else — if it still
 * overlaps the table at all — the cell it overlaps, and only out to the board
 * once it is clear of the grid (`reanchorForDrop` owns that ladder and the rule
 * behind it: an item on the table always belongs to exactly one cell). The plan
 * this returns names exactly three things: the anchor to write (or not), the
 * geometry keys the gesture actually changed, and the cells whose size must be
 * readjusted afterwards.
 *
 * Two rules are load-bearing and are why this is a separate, tested module
 * rather than inline drag code:
 *
 *  - **C2 — never re-write an unchanged key.** `x`/`y` are independent LWW
 *    keys; writing a stale `y` on a purely horizontal drag silently reverts a
 *    peer who moved the same element vertically. A board→board move writes only
 *    the axes that moved, and a move that ends cell-anchored writes no geometry
 *    at all (a contained element renders from its cell + offset — its `x`/`y`
 *    are a fallback cache the render ignores).
 *  - **One planner, one rule.** A single drag and an F-34 GROUP drag differ
 *    only in how many plans get applied in the one transaction
 *    (`session.applyElementMoves`), so a multi-select drag cannot drift from
 *    the behavior of dragging the same element on its own.
 */
import type { Anchor, ElementInfo } from "@kola/shared";

import type { ElementMovePlan } from "../../core/session.js";
import type { GridLayoutSnapshot } from "../board/GridTable.js";
import { anchorsEqual, elementConfinesToCell, reanchorForDrop } from "./contained.js";

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Plan the commit for one element moved from `orig` to `final` (absolute board
 * rects). `element` needs only its identity, type and CURRENT anchor.
 *
 * `pointer` is the cursor's board position at release — the point that decides
 * which cell the drop belongs to. Only the element UNDER the pointer has one;
 * a group-drag follower passes null and homes by its own top-left.
 *
 * Cell re-fits (F-26/F-36): the source cell shrinks back toward the defaults
 * when the element left it or moved up/left inside it, and the destination
 * grows so the drop is never clipped. A same-cell move re-fits once (the source
 * fit already covers it). A DRAWING never sizes a cell — it is cell-anchored
 * only to follow its start cell — so it plans no fits at all.
 */
export function planElementMove(
  element: Pick<ElementInfo, "id" | "type" | "anchor">,
  layout: GridLayoutSnapshot | null,
  orig: Rect,
  final: Rect,
  pointer: { x: number; y: number } | null = null,
): ElementMovePlan {
  // Round the top-left ONCE so the derived dx/dy (and any board fallback) are
  // whole board units and the two stay exactly consistent.
  const x = Math.round(final.x);
  const y = Math.round(final.y);
  const prev = element.anchor;
  const next: Anchor = reanchorForDrop({ ...final, x, y }, pointer, layout);

  const plan: ElementMovePlan = { elementId: element.id, patch: {}, fitCells: [] };

  // A board→board move needs no anchor write: the render reads x/y, the stale
  // board anchor's own coordinates are never read, and skipping the whole-object
  // write keeps the commit to only the keys this gesture changed.
  if (!(next.kind === "board" && prev.kind === "board") && !anchorsEqual(next, prev)) {
    plan.anchor = next;
  }

  if (next.kind === "board") {
    // Coming FROM a cell the x/y cache is stale, so both axes are written;
    // a board→board move writes only the axes that actually moved (C2).
    if (prev.kind !== "board" || final.x !== orig.x) plan.patch.x = x;
    if (prev.kind !== "board" || final.y !== orig.y) plan.patch.y = y;
  }

  if (elementConfinesToCell(element.type)) {
    if (prev.kind === "cell") plan.fitCells.push({ rowId: prev.rowId, colId: prev.colId });
    const sameCell =
      prev.kind === "cell" &&
      next.kind === "cell" &&
      prev.rowId === next.rowId &&
      prev.colId === next.colId;
    if (next.kind === "cell" && !sameCell) {
      plan.fitCells.push({ rowId: next.rowId, colId: next.colId });
    }
  }

  return plan;
}

/**
 * Offset a rect by a drag delta — how a GROUP move derives each follower's
 * final rect from its own start rect. Every element in the selection moves by
 * the SAME board-space delta as the one under the pointer, so their relative
 * layout is preserved exactly, and each still re-homes independently through
 * `planElementMove` (one may land in a cell while another lands on the board).
 */
export function offsetRect(rect: Rect, dx: number, dy: number): Rect {
  return { x: rect.x + dx, y: rect.y + dy, w: rect.w, h: rect.h };
}
